import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import {
  Button,
  InputGroup,
  Form,
  Row,
  Col,
  Container,
  Spinner,
  Badge,
} from 'react-bootstrap';
import Select from 'react-select';
import { FaFileExcel, FaSearch, FaBoxOpen } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DataTable from 'react-data-table-component';
import { useReportData } from '../hooks/useReportData';
import {
  authAxios,
  formatDateTime12Hr,
  calculateTimeTaken,
} from '../utils/reportUtils';

const AllOrdersReport = () => {
  const {
    allOrders,
    loading,
    isFetchingMore,
    hasMore,
    dateFrom,
    dateTo,
    setDateFrom,
    setDateTo,
    handleSearch,
    handleClearFilters,
    isFetchingRef,
    total,
    currentPage,
    lastPage,
  } = useReportData();

  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedShowroom, setSelectedShowroom] = useState(null);
  const [showrooms, setShowrooms] = useState([]);

  const statusOptions = [
    'pending',
    'assigned',
    'accepted',
    'rejected',
    'picked_up',
    'in_transit',
    'delivered',
    'returned',
    'cancelled',
    'unfulfilled',
  ].map((s) => ({ value: s, label: s.replace('_', ' ').toUpperCase() }));

  const fetchShowrooms = async () => {
    try {
      const response = await authAxios.get('/showrooms');
      const rawList =
        response.data?.data?.data?.data ||
        response.data?.data?.data ||
        response.data?.data ||
        [];
      const formatted = rawList.map((s) => ({
        value: s.name,
        label: s.name,
        id: s.id,
      }));
      setShowrooms(formatted);
    } catch (error) {
      console.error('❌ Failed to fetch showrooms:', error);
    }
  };

  useEffect(() => {
    fetchShowrooms();
  }, []);

  // Client-side filtering
  useEffect(() => {
    let filtered = allOrders;

    if (search) {
      filtered = filtered.filter(
        (o) =>
          o.id?.toString().includes(search) ||
          o.type?.toLowerCase().includes(search.toLowerCase()) ||
          o.status?.toLowerCase().includes(search.toLowerCase()) ||
          o.driver?.name?.toLowerCase().includes(search.toLowerCase()) ||
          (o.customer?.name || o.vendor?.name)
            ?.toLowerCase()
            .includes(search.toLowerCase())
      );
    }

    if (selectedStatus?.value)
      filtered = filtered.filter((o) => o.status === selectedStatus.value);

    if (selectedShowroom && selectedShowroom.value)
      filtered = filtered.filter(
        (o) => o.showroom?.name === selectedShowroom.value
      );

    setOrders(filtered);
  }, [allOrders, search, selectedStatus, selectedShowroom]);

  const getOrderStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'picked up':
      case 'delivered':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'in_transit':
      case 'assigned':
      case 'accepted':
        return 'primary';
      case 'cancelled':
      case 'rejected':
      case 'returned':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const orderColumns = [
    {
      name: '#',
      selector: (row, index) => index + 1,
    },
    {
      name: 'PO Number',
      selector: (row) => row.po_number,
      sortable: true,
    },
    {
      name: 'Order Type',
      selector: (row) => row.type,
      sortable: true,
      cell: (row) => {
        if (!row.type) return 'N/A';
        return row.type
          .replace(/_/g, ' ')
          .split(' ')
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
      },
    },
    {
      name: 'Showroom',
      selector: (row) => row.showroom?.name || 'N/A',
      sortable: true,
    },
    {
      name: 'Driver',
      selector: (row) => row.driver?.name || 'N/A',
      sortable: true,
    },
    {
      name: 'Customer/Vendor',
      selector: (row) => row.customer?.name || row.vendor?.name || 'N/A',
      sortable: true,
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        if (!row.status) return 'N/A';
        const statusText = row.status
          .replace(/_/g, ' ')
          .split(' ')
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ');
        return (
          <Badge bg={getOrderStatusVariant(row.status)}>{statusText}</Badge>
        );
      },
    },
    {
      name: 'Order Created At',
      selector: (row) => formatDateTime12Hr(row.created_at),
      sortable: true,
    },
    {
      name: 'Order Updated At',
      selector: (row) => formatDateTime12Hr(row.updated_at),
      sortable: true,
    },
    /*{
      name: 'Time Taken',
      selector: (row) => {
        const logs = row.status_logs || row.statusLogs || [];
        const pickedUp = logs.find((l) => l.to_status === 'picked_up')?.created_at;
        const delivered = logs.find((l) => l.to_status === 'delivered')?.created_at;
        return calculateTimeTaken(pickedUp, delivered) || 'N/A';
      },
      sortable: true,
    },*/
  ];

  const handleExportToExcel = () => {
    if (!orders.length) {
      alert('No data to export');
      return;
    }

    const exportData = orders.map((row) => {
      const logs = row.status_logs || row.statusLogs || [];
      return {
        'PO Number': row.po_number || 'N/A',
        'Order Type': row.type,
        Showroom: row.showroom?.name || 'N/A',
        Driver: row.driver?.name || 'N/A',
        'Customer/Vendor': row.customer?.name || row.vendor?.name || 'N/A',
        Status: row.status,
        'Order Created At': formatDateTime12Hr(row.created_at),
        'Order Updated At': row.updated_at
          ? formatDateTime12Hr(row.updated_at)
          : '',
        'Time Taken for Delivery': calculateTimeTaken(
          logs.find((l) => l.to_status === 'picked_up')?.created_at,
          logs.find((l) => l.to_status === 'delivered')?.created_at
        ),
      };
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'All Orders Report');
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], {
        type: 'application/octet-stream',
      }),
      `all_orders_report_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  // Removed Intersection Observer - now fetching all pages sequentially via useReportData hook

  const CustomLoadingIndicator = (
    <div className='p-3 text-center'>
      <Spinner
        animation='border'
        role='status'
        className='me-2'
        variant='primary'
      />
      <span className='text-primary'>
        {currentPage && lastPage && lastPage > 1
          ? `Loading orders... (Page ${currentPage} of ${lastPage})`
          : 'Loading orders...'}
      </span>
    </div>
  );

  const CustomNoDataComponent = (
    <div className='p-4 text-center text-muted'>
      <FaBoxOpen className='mb-2' size={32} />
      <h5 className='mt-2'>No Data Found</h5>
      <p className='mb-0'>
        There is currently no data in this report or matching your search
        criteria.
      </p>
    </div>
  );

  return (
    <Container fluid>
      <h3 style={{ textAlign: 'center', marginBottom: '20px' }}>
        All Orders Report
      </h3>

      {/* Filters */}
      <Row className='justify-content-between align-items-center mt-3 mb-3'>
        <Col md={8}>
          <Row className='g-2'>
            <Col xs='auto'>
              <InputGroup style={{ width: '300px' }}>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  type='text'
                  placeholder='Search...'
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs='auto' style={{ width: '200px' }}>
              <Select
                options={[
                  { value: null, label: 'All Status' },
                  ...statusOptions,
                ]}
                onChange={(opt) => setSelectedStatus(opt)}
                value={selectedStatus}
                isClearable
                placeholder='Filter Status...'
              />
            </Col>
            <Col xs='auto' style={{ width: '200px' }}>
              <Select
                options={showrooms}
                onChange={(opt) => setSelectedShowroom(opt)}
                value={selectedShowroom}
                isClearable
                placeholder='Filter Showroom...'
              />
            </Col>
            <Col xs='auto'>
              <div className='d-flex align-items-center gap-2'>
                <Form.Control
                  type='date'
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  title='Date From'
                  style={{ width: '150px' }}
                />
                <span>to</span>
                <Form.Control
                  type='date'
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  title='Date To'
                  style={{ width: '150px' }}
                />
                <Button
                  variant='primary'
                  size='sm'
                  onClick={handleSearch}
                  disabled={loading}
                >
                  <FaSearch className='me-1' />
                  Search
                </Button>
                <Button
                  variant='outline-secondary'
                  size='sm'
                  onClick={handleClearFilters}
                  disabled={loading}
                >
                  Clear
                </Button>
              </div>
            </Col>
          </Row>
        </Col>

        <Col md={4} className='text-end'>
          <Button size='sm' variant='success' onClick={handleExportToExcel}>
            <FaFileExcel /> Export
          </Button>
        </Col>
      </Row>

      {/* Table */}
      <div style={{ maxHeight: '59vh', position: 'relative' }}>
        {/* Count Display */}
        {orders.length > 0 && (
          <div
            className='text-end p-2'
            style={{
              backgroundColor: '#f8f9fa',
              borderBottom: '1px solid #dee2e6',
            }}
          >
            <small className='text-muted'>
              Showing <strong>{1}</strong> to <strong>{orders.length}</strong>{' '}
              out of <strong>{total}</strong> orders
            </small>
          </div>
        )}
        <DataTable
          columns={orderColumns}
          data={orders}
          pagination={false}
          highlightOnHover
          striped
          dense
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight='50vh'
          customStyles={{
            table: {
              style: {
                tableLayout: 'auto',
                width: '100%',
                maxWidth: '100%',
              },
            },
            tableWrapper: {
              style: {
                width: '100%',
                maxWidth: '100%',
                overflowX: 'auto',
              },
            },
            headRow: {
              style: {
                backgroundColor: '#f8f9fa',
                borderBottomStyle: 'solid',
                borderBottomWidth: '2px',
                borderBottomColor: '#dee2e6',
              },
            },
            headCells: {
              style: {
                fontSize: '14px',
                fontWeight: '600',
                paddingLeft: '8px',
                paddingRight: '8px',
              },
            },
            cells: {
              style: {
                wordBreak: 'break-word',
                whiteSpace: 'normal',
              },
            },
          }}
          progressPending={loading}
          progressComponent={CustomLoadingIndicator}
          noDataComponent={CustomNoDataComponent}
        />
        {isFetchingMore && (
          <div
            className='text-center p-2 text-muted'
            style={{ fontSize: '0.875rem' }}
          >
            <Spinner
              animation='border'
              role='status'
              className='me-2'
              size='sm'
            />
            <span>
              Fetching more orders... (Page {currentPage} of {lastPage || '?'})
            </span>
          </div>
        )}
        {!loading && !isFetchingMore && allOrders.length > 0 && (
          <div className='text-center p-3 text-muted'>
            <small>All orders loaded ({allOrders.length} total)</small>
          </div>
        )}
      </div>
    </Container>
  );
};

export default AllOrdersReport;
